// Package Imports
import { createRoute, lazyRouteComponent } from '@tanstack/react-router'

// Core Imports
import PageSkeleton from '@/core/components/page-skeleton'
import RouteError from '@/core/components/route-error'
import { queryClient } from '@/core/lib/query-client'
import { routeHasPermission } from '@/core/utils/auth-utils'

// External Module Imports
import { dashboardRoute } from '@/modules/dashboard/dashboard.routes'

// Module Imports
import { periodicSearchSchema } from '@/modules/periodic-generation/periodic-generation.schemas'
import {
  availableDraftsQueryOptions,
  periodicDetailQueryOptions,
  periodicListQueryOptions,
} from '@/modules/periodic-generation/periodic-generation.hooks'
import { periodOptionsQueryOptions } from '@/modules/period/period.hooks'

export const periodicGenerationRoute = createRoute({
  path: '/periodic-generation',
  getParentRoute: () => dashboardRoute,
  errorComponent: ({ error, reset }) => (
    <RouteError error={error} reset={reset} />
  ),
})

export const periodicListRoute = createRoute({
  getParentRoute: () => periodicGenerationRoute,
  path: '/',
  validateSearch: periodicSearchSchema,
  loaderDeps: ({ search }) => ({
    page: search.page ?? 1,
    query: search.query ?? '',
    status: search.status ?? 'ALL',
  }),
  beforeLoad: ({ preload }) =>
    routeHasPermission(
      preload,
      'periodic.list',
      'Periyodik üretime erişim izniniz yok.'
    ),
  loader: ({ deps }) =>
    queryClient.ensureQueryData(
      periodicListQueryOptions(deps.page, deps.query, deps.status)
    ),
  head: () => ({ meta: [{ title: 'Periyodik Üretim' }] }),
  pendingComponent: () => <PageSkeleton />,
  pendingMs: 100,
  component: lazyRouteComponent(
    () => import('@/modules/periodic-generation/pages/list-periodic.page')
  ),
})

export const periodicCreateRoute = createRoute({
  getParentRoute: () => periodicGenerationRoute,
  path: '/new',
  beforeLoad: ({ preload }) =>
    routeHasPermission(
      preload,
      'periodic.create',
      'Periyodik üretim oluşturmak için yetkiniz yok.'
    ),
  loader: async () => {
    await queryClient.ensureQueryData(availableDraftsQueryOptions())
    await queryClient.ensureQueryData(periodOptionsQueryOptions())
  },
  head: () => ({ meta: [{ title: 'Yeni Periyodik Üretim' }] }),
  pendingComponent: () => <PageSkeleton />,
  pendingMs: 100,
  component: lazyRouteComponent(
    () => import('@/modules/periodic-generation/pages/create-periodic.page')
  ),
})

export const periodicEditRoute = createRoute({
  getParentRoute: () => periodicGenerationRoute,
  path: '$id/edit',
  beforeLoad: ({ preload }) =>
    routeHasPermission(
      preload,
      'periodic.update',
      'Periyodik üretim düzenlemek için yetkiniz yok.'
    ),
  loader: async ({ params }) => {
    const detail = await queryClient.ensureQueryData(
      periodicDetailQueryOptions(params.id)
    )
    await queryClient.ensureQueryData(
      availableDraftsQueryOptions(detail.draft_batch?.id)
    )
    await queryClient.ensureQueryData(periodOptionsQueryOptions())
  },
  head: () => ({ meta: [{ title: 'Periyodik Üretimi Düzenle' }] }),
  pendingComponent: () => <PageSkeleton />,
  pendingMs: 100,
  component: lazyRouteComponent(
    () => import('@/modules/periodic-generation/pages/edit-periodic.page')
  ),
})
